/**
 * Fleet presence and quick messaging.
 *
 * Two jobs:
 *  - broadcast where this driver is, and keep a roster of everyone else with
 *    the bearing and distance to them,
 *  - carry three fixed questions between drivers ("food?", "fuel stop?",
 *    "help!") and their yes/no answers.
 *
 * The wire format is an ENUM, never a sentence. 'food' | 'fuel' | 'help' goes
 * over the network and each app renders and speaks it in ITS OWN language, so a
 * Thai-speaking driver receives Thai even though the sender was on English.
 * Sending prose would have forced the sender's language on the recipient and
 * pushed foreign script through the wrong TTS voice - exactly the failure this
 * release exists to remove.
 *
 * Everything here is best-effort. The backend is polled, not streamed (shared
 * hosting, no websockets); a failed poll is silent and the next one recovers.
 */

import type { Fix, LngLat } from '../core/types';
import { bearing, bearingDelta, destinationPoint, distance } from '../core/geo';

const API = `${import.meta.env.BASE_URL}api`;

export type MessageKind = 'food' | 'fuel' | 'help' | 'dest';
export type MessageReply = 'yes' | 'no';

/** Payload of a 'dest' message: a destination being shared with a driver. */
export interface SharedDest {
  lng: number;
  lat: number;
  name: string;
}

export interface FleetDriver {
  id: number;
  name: string;
  lng: number;
  lat: number;
  heading: number | null;
  speed: number | null;
  destination: string | null;
  /** epoch ms of their last report */
  at: number;
}

/** A driver plus where they are relative to US. */
export interface FleetContact extends FleetDriver {
  /** metres, straight line */
  distance: number;
  /** compass bearing from us to them */
  bearing: number;
  /** bearing relative to our heading: 0 = dead ahead, 180 = behind */
  relativeBearing: number;
  /** true when their last report is old enough to distrust */
  stale: boolean;
}

export interface QuickMessage {
  id: string;
  from: number;
  fromName: string;
  to: number;
  kind: MessageKind;
  /** Present only on kind 'dest'. */
  dest?: SharedDest | null;
  reply: MessageReply | null;
  at: number;
  repliedAt: number | null;
}

/** Reports older than this are shown greyed out rather than trusted. */
const STALE_MS = 120_000;

/** Presence is deliberately calmer than messaging; location is projected between reports. */
const PRESENCE_POLL_MS = 30_000;
/** Do not dead-reckon indefinitely when a phone has stopped reporting. */
const MAX_PROJECTION_MS = 45_000;

export interface FleetEvents {
  /** Roster changed. */
  onDrivers?: (contacts: FleetContact[]) => void;
  /** A question has arrived for us and needs a yes/no. */
  onIncoming?: (message: QuickMessage) => void;
  /**
   * Someone answered a question we asked. `announce` is false when the answer
   * is only being adopted into the roster (backlog on a cold start), true when
   * it genuinely just arrived and deserves the driver's attention.
   */
  onReply?: (message: QuickMessage, announce: boolean) => void;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    signal: AbortSignal.timeout(12_000),
    ...init,
  });
  if (!res.ok) throw new Error(String(res.status));
  return (await res.json()) as T;
}

export class Fleet {
  private drivers: FleetDriver[] = [];
  private contacts: FleetContact[] = [];
  private lastFix: Fix | null = null;
  private since = 0;
  private seenIncoming = new Set<string>();
  private seenReplies = new Set<string>();
  /** False until the first poll lands; see pollMessages for why that matters. */
  private primed = false;
  private timers: number[] = [];
  private destinationName: string | null = null;

  constructor(private events: FleetEvents = {}) {}

  start(): void {
    this.stop();
    // Reports are projected using heading and speed between polls, so a calmer
    // 30 s cadence stays fluid without making two motorway cars jump 500 m at
    // each network response.
    this.timers.push(window.setInterval(() => void this.push(), PRESENCE_POLL_MS));
    this.timers.push(window.setInterval(() => void this.pull(), PRESENCE_POLL_MS));
    // Messages need to feel immediate, so they get their own faster loop.
    this.timers.push(window.setInterval(() => void this.pollMessages(), 8_000));
    void this.pull();
    void this.pollMessages();
  }

  stop(): void {
    for (const id of this.timers) clearInterval(id);
    this.timers = [];
  }

  /** Feed the latest GPS fix; also recomputes bearings to everyone. */
  setFix(fix: Fix): void {
    this.lastFix = fix;
    this.recompute();
  }

  setDestinationName(name: string | null): void {
    this.destinationName = name;
  }

  get roster(): FleetContact[] {
    return this.contacts;
  }

  private async push(): Promise<void> {
    const f = this.lastFix;
    if (!f) return;
    try {
      await call('/presence', {
        method: 'POST',
        body: JSON.stringify({
          lng: f.lng,
          lat: f.lat,
          heading: f.heading,
          speed: f.speed,
          destination: this.destinationName,
        }),
      });
    } catch {
      /* offline or backend down - the next tick retries */
    }
  }

  private async pull(): Promise<void> {
    try {
      const { drivers } = await call<{ drivers: FleetDriver[] }>('/presence');
      this.drivers = drivers;
      this.recompute();
    } catch {
      /* keep the last known roster rather than blanking the bubble */
    }
  }

  private recompute(): void {
    const f = this.lastFix;
    const here: LngLat | null = f ? [f.lng, f.lat] : null;
    const now = Date.now();

    this.contacts = this.drivers
      .map((d): FleetContact => {
        const reported: LngLat = [d.lng, d.lat];
        const age = Math.max(0, Math.min(MAX_PROJECTION_MS, now - d.at));
        const canProject =
          d.heading != null && Number.isFinite(d.heading) &&
          d.speed != null && Number.isFinite(d.speed) && d.speed >= 2 &&
          now - d.at <= STALE_MS;
        const there = canProject
          ? destinationPoint(reported, d.heading!, d.speed! * (age / 1000))
          : reported;
        const b = here ? bearing(here, there) : 0;
        return {
          ...d,
          // Keep every fleet surface on the same dead-reckoned position. The
          // map must not fall back to the older raw report while its distance
          // bubble is already pointing at the projected car.
          lng: there[0],
          lat: there[1],
          distance: here ? distance(here, there) : 0,
          bearing: b,
          relativeBearing:
            here && f?.heading != null ? bearingDelta(f.heading, b) : b,
          stale: now - d.at > STALE_MS,
        };
      })
      .sort((a, b) => a.distance - b.distance);

    this.events.onDrivers?.(this.contacts);
  }

  private async pollMessages(): Promise<void> {
    try {
      const body = await call<{ inbox: QuickMessage[]; replies: QuickMessage[]; now: number }>(
        `/messages?since=${this.since}`,
      );
      this.since = body.now;

      for (const msg of body.inbox) {
        // The backend keeps returning unanswered messages; only surface each once.
        if (this.seenIncoming.has(msg.id)) continue;
        this.seenIncoming.add(msg.id);
        this.events.onIncoming?.(msg);
      }

      /*
       * A fresh app start polls with since=0, so the backend hands back every
       * answer still inside its 2 h retention. Announcing those would tell a
       * driver who just opened the app that "Bobby: Yes" had only now arrived,
       * when it may be an hour stale and about a stop already passed.
       *
       * So the first poll only ADOPTS the backlog - it fills the roster's
       * answer column, which is what makes a missed toast recoverable - and
       * anything genuinely recent still speaks up.
       */
      for (const msg of body.replies) {
        if (this.seenReplies.has(msg.id)) continue;
        this.seenReplies.add(msg.id);
        const fresh = Date.now() - (msg.repliedAt ?? msg.at) < 90_000;
        this.events.onReply?.(msg, this.primed || fresh);
      }
      this.primed = true;
    } catch {
      /* silent - a dropped poll is not worth telling a driver about */
    }
  }

  /** Ask one driver a fixed question, or share a destination (kind 'dest'). */
  async send(to: number, kind: MessageKind, dest?: SharedDest): Promise<void> {
    await call('/messages', { method: 'POST', body: JSON.stringify({ to, kind, dest }) });
  }

  /** Answer a question addressed to us. */
  async reply(id: string, reply: MessageReply): Promise<void> {
    // Mark handled immediately so a slow round-trip can't re-prompt.
    this.seenIncoming.add(id);
    await call(`/messages/${id}/reply`, { method: 'POST', body: JSON.stringify({ reply }) });
  }
}
